/**
 * Reconnect policy for `useSSEStream`, as a pure function so it can be tested without a DOM.
 *
 * A browser cannot tell a 403/404 from a network drop, so a stream whose access was revoked would otherwise be
 * re-requested at the maximum backoff for as long as the page stays open. After `MAX_CONSECUTIVE_FAILURES` failed
 * connects (no `open` in between) the hook stops and reports "stopped" with a visible Retry; a short outage, a deploy
 * or a laptop sleep therefore ends in a state the user can act on instead of silently losing live updates.
 */

export const MIN_BACKOFF_MS = 2000;
export const MAX_BACKOFF_MS = 30000;
export const BACKOFF_MULTIPLIER = 2;
export const MAX_CONSECUTIVE_FAILURES = 8;

export interface ReconnectPlan {
  /** Consecutive failures including the one just seen. */
  failures: number;
  /** The backoff to carry into the next failure. */
  backoffMs: number;
  /** Wait this long, then reconnect; `null` means stop and surface "stopped". */
  delayMs: number | null;
}

/** What to do after a failed connect, given the failures so far and the current backoff. */
export function planReconnect(
  failuresSoFar: number,
  backoffMs: number,
): ReconnectPlan {
  const failures = failuresSoFar + 1;
  if (failures >= MAX_CONSECUTIVE_FAILURES) {
    return { failures, backoffMs, delayMs: null };
  }
  return {
    failures,
    backoffMs: Math.min(backoffMs * BACKOFF_MULTIPLIER, MAX_BACKOFF_MS),
    delayMs: backoffMs,
  };
}
